import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, DollarSign, CreditCard, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.1 },
  },
};

export default function Senders() {
  const { t } = useLanguage();

  const steps = [
    t('senders.step1'),
    t('senders.step2'),
    t('senders.step3'),
    t('senders.step4'),
  ];

  const benefits = [
    {
      icon: Clock,
      title: t('senders.benefit1'),
      description: t('senders.benefit1Desc'),
      color: 'emerald',
    },
    {
      icon: DollarSign,
      title: t('senders.benefit2'),
      description: t('senders.benefit2Desc'),
      color: 'blue',
    },
    {
      icon: CreditCard,
      title: t('senders.benefit3'),
      description: t('senders.benefit3Desc'),
      color: 'purple',
    },
    {
      icon: CheckCircle,
      title: t('senders.benefit4'),
      description: t('senders.benefit4Desc'),
      color: 'amber',
    },
  ];

  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-100',
      icon: 'text-emerald-600',
      hoverBg: 'group-hover:bg-emerald-600',
    },
    blue: {
      bg: 'bg-blue-100',
      icon: 'text-blue-600',
      hoverBg: 'group-hover:bg-blue-600',
    },
    purple: {
      bg: 'bg-purple-100',
      icon: 'text-purple-600',
      hoverBg: 'group-hover:bg-purple-600',
    },
    amber: {
      bg: 'bg-amber-100',
      icon: 'text-amber-600',
      hoverBg: 'group-hover:bg-amber-500',
    },
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 py-20 md:py-28" data-testid="senders-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeInUp}>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
                {t('senders.title')}
              </h1>
              <p className="mt-6 text-lg text-emerald-100 leading-relaxed max-w-xl">
                {t('senders.subtitle')}
              </p>
              <div className="mt-8">
                <Link to="/app/sender">
                  <Button
                    data-testid="senders-cta"
                    className="h-12 px-8 rounded-full bg-white text-emerald-900 hover:bg-emerald-50 font-semibold shadow-lg shadow-emerald-900/30"
                  >
                    {t('senders.ctaButton')}
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp} className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1683650666910-ecc317a12061?w=2400&h=1800&fit=crop&q=100"
                  alt="Happy Latino family"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="senders-how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
              {t('senders.howTitle')}
            </motion.h2>
            <div className="max-w-3xl mx-auto">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-start gap-4 mb-6 last:mb-0"
                  data-testid={`senders-step-${index + 1}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 pt-2">
                    <p className="text-lg text-slate-700">{step}</p>
                    {index < steps.length - 1 && (
                      <div className="ml-5 mt-3 mb-3 w-0.5 h-8 bg-emerald-200" />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28" data-testid="senders-benefits">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
              {t('senders.benefitsTitle')}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                const colors = colorClasses[benefit.color];
                return (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="group relative p-8 bg-white rounded-3xl border border-slate-100 hover:border-emerald-100 transition-all duration-500 hover:shadow-lg"
                    data-testid={`senders-benefit-${index + 1}`}
                  >
                    <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center mb-6 ${colors.hoverBg} transition-colors`}>
                      <Icon className={`w-7 h-7 ${colors.icon} group-hover:text-white transition-colors`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-emerald-600 to-emerald-700" data-testid="senders-cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('senders.ctaTitle')}
            </motion.h2>
            <motion.div variants={fadeInUp}>
              <Link to="/app/sender">
                <Button
                  data-testid="senders-final-cta"
                  className="h-12 px-8 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-semibold shadow-lg inline-flex items-center gap-2"
                >
                  {t('senders.ctaButton')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
